import { useTheme } from "@mui/material";
import { createSxStyles } from "../../createSxStyles";
import { listPageToolbarIconButtonSize } from "@common/components/listPageToolbar/ListPageToolbar.styles";
import { statusChipColors } from "@common/components/statusChip/StatusChip";

export const useSavedViewsMenuStyles = () => {
    const theme = useTheme();
    return createSxStyles({
        // Fits the view's name up to 250px, with a longer name cut off with an ellipsis. A fixed max and not a percentage:
        // the toolbar sizes its button row from the buttons' natural widths, where a percentage counts as no max at all, so
        // the row would make room for the whole name and push the buttons left of the toolbar's edge
        viewPicker: {
            maxWidth: 250,
            minWidth: 0,
            "& .MuiButton-root": {
                width: "100%",
                maxWidth: "100%",
                minHeight: listPageToolbarIconButtonSize,
                gap: 1,
                justifyContent: "flex-start",
                textTransform: "none",
            },
            // DotLegalButton sets its padding again on hover, at the same specificity as the rule above, so without
            // the hover selector the stylesheet order decides and the content can shift when the pointer enters
            "& .MuiButton-root, & .MuiButton-root:hover": {
                paddingLeft: "4px",
                paddingRight: "12px",
                paddingY: 0.5,
            },
        },
        viewPickerText: {
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            minWidth: 0,
            textAlign: "left",
            // Open Sans sits low in its line box, so centred lowercase text reads lower than the icons beside it
            transform: "translateY(-1px)",
        },
        // Not a DotLegalHeader: its own overflow tooltip would open on top of the changed tooltip, and it does not
        // expose whether the name is cut short, so the name could not join that tooltip
        viewPickerName: {
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: theme.typography.pxToRem(14),
            fontWeight: 400,
            color: theme.palette.primary.dark,
        },
        tooltipViewName: {
            fontWeight: 600,
            "&:not(:last-child)": { marginBottom: 0.5 },
        },
        tooltipChangedLine: {
            display: "flex",
            alignItems: "center",
            gap: 0.75,
        },
        tooltipChangedDot: {
            flexShrink: 0,
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: statusChipColors.yellow.dot,
        },
        // The bookmark is drawn narrower than its square box, so the dot moves in from the box's corner to the bookmark's own
        changedViewDot: {
            "& .MuiBadge-badge": { right: 3 },
        },
        footerAddIcon: {
            fontSize: "18px",
            color: theme.palette.primary.dark,
        },
        changedViewAlert: {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            "& .MuiAlert-icon": { display: "none" },
            "& .MuiAlert-message": { fontSize: 12 },
            "& .MuiAlertTitle-root": { fontSize: 13 },
        },
        sectionLabel: {
            lineHeight: "normal",
            fontSize: 11,
            fontWeight: 600,
            color: theme.palette.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            padding: theme.spacing(1.5, 1, 0.5, 1),
        },
        viewRow: {
            borderRadius: "4px",
            paddingY: 0,
            paddingX: 1,
            height: "34px",
            "& .MuiListItemIcon-root": { minWidth: 24 },
        },
        selectedViewRow: {
            backgroundColor: theme.palette.primary.light,
        },
        footerDivider: {
            // Doubled so it outranks the menu item's own rule that pads a divider following it on both sides
            "&&": {
                marginTop: 1,
                marginBottom: 0,
            },
        },
        footerRow: {
            backgroundColor: theme.palette.grey[50],
            height: "34px",
            borderRadius: "4px",
            paddingX: 1,
            "& .MuiListItemIcon-root": { minWidth: 24 },
        },
        menuPaper: {
            borderRadius: "8px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            border: `1px solid ${theme.palette.divider}`,
            minWidth: "320px",
            maxWidth: "500px",
        },
    });
};
